'use client';

import { useState, useEffect, useRef, useCallback } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import Icon from '@/components/ui/AppIcon';
import AppImage from '@/components/ui/AppImage';
import { useActiveSection } from '@/hooks/useActiveSection';

interface HeaderProps {
  className?: string;
}

const Header = ({ className = '' }: HeaderProps) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const pathname = usePathname();
  const isHomepage = pathname === '/homepage' || pathname === '/';

  const menuButtonRef = useRef<HTMLButtonElement>(null);
  const drawerRef = useRef<HTMLDivElement>(null);

  const activeSection = useActiveSection(['hero', 'who-we-serve', 'what-we-build', 'work', 'approach']);

  const navigationItems = isHomepage
    ? [
        { label: 'Home', href: '#hero', isAnchor: true },
        { label: 'Who We Serve', href: '#who-we-serve', isAnchor: true },
        { label: 'What We Build', href: '#what-we-build', isAnchor: true },
        { label: 'Work', href: '#work', isAnchor: true },
        { label: 'Approach', href: '#approach', isAnchor: true },
      ]
    : [
        { label: 'Home', href: '/homepage', isAnchor: false },
        { label: 'Work', href: '/portfolio', isAnchor: false },
        { label: 'Services', href: '/services', isAnchor: false },
        { label: 'About', href: '/about', isAnchor: false },
        { label: 'Contact', href: '/contact', isAnchor: false },
      ];

  const openMenu = useCallback(() => {
    setIsMobileMenuOpen(true);
    setIsAnimating(true);
  }, []);

  const closeMenu = useCallback(() => {
    setIsAnimating(false);
    // Wait for close animation to finish before unmounting
    setTimeout(() => {
      setIsMobileMenuOpen(false);
      menuButtonRef.current?.focus();
    }, 300);
  }, []);

  const toggleMobileMenu = useCallback(() => {
    if (isMobileMenuOpen) {
      closeMenu();
    } else {
      openMenu();
    }
  }, [isMobileMenuOpen, openMenu, closeMenu]);

  const handleLinkClick = useCallback(() => {
    closeMenu();
  }, [closeMenu]);

  // Focus trap inside drawer
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (!isMobileMenuOpen) return;

      if (e.key === 'Escape') {
        e.preventDefault();
        closeMenu();
        return;
      }

      if (e.key === 'Tab') {
        const drawer = drawerRef.current;
        if (!drawer) return;

        const focusableSelectors =
          'a[href], button:not([disabled]), [tabindex]:not([tabIndex="-1"])';
        const focusableElements = Array.from(
          drawer.querySelectorAll<HTMLElement>(focusableSelectors)
        );

        if (focusableElements.length === 0) return;

        const firstEl = focusableElements[0];
        const lastEl = focusableElements[focusableElements.length - 1];

        if (e.shiftKey) {
          if (document.activeElement === firstEl) {
            e.preventDefault();
            lastEl.focus();
          }
        } else {
          if (document.activeElement === lastEl) {
            e.preventDefault();
            firstEl.focus();
          }
        }
      }
    },
    [isMobileMenuOpen, closeMenu]
  );

  // Attach/detach keyboard listener
  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  // Move focus into drawer when it opens
  useEffect(() => {
    if (isMobileMenuOpen && isAnimating) {
      const drawer = drawerRef.current;
      if (!drawer) return;
      const firstFocusable = drawer.querySelector<HTMLElement>(
        'a[href], button:not([disabled])'
      );
      // Small delay to let the animation start before focusing
      const timer = setTimeout(() => firstFocusable?.focus(), 50);
      return () => clearTimeout(timer);
    }
  }, [isMobileMenuOpen, isAnimating]);

  // Prevent body scroll when drawer is open
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMobileMenuOpen]);

  const isActive = (item: typeof navigationItems[0]) => {
    if (item.isAnchor) {
      const sectionId = item.href.replace('#', '');
      return activeSection === sectionId;
    }
    return pathname === item.href;
  };

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 backdrop-blur-sm ${className}`}>
      <div className="w-full">
        <div className="flex items-center justify-between h-20 md:h-24 px-3 sm:px-4 md:px-6 lg:px-8">
          {/* Logo */}
          <Link href="/homepage" className="flex items-center hover:opacity-80 transition-smooth min-h-[44px] min-w-[44px]">
            <div className="w-[70px] h-[70px] md:w-[80px] md:h-[80px]">
              <AppImage
                src="/assets/images/Lone_Star_Local_Full_Color-1771711306028.png"
                alt="The Lone Star Local logo"
                width={80}
                height={80}
                className="w-full h-full object-contain drop-shadow-lg"
              />
            </div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-1">
            {navigationItems.map((item) => {
              const active = isActive(item);
              return (
                <Link
                  key={item.href}
                  href={item.href}
                  className={`px-3 xl:px-4 py-2 text-sm font-body font-medium rounded-md transition-all duration-300 relative ${
                    active
                      ? 'text-brand-teal bg-white/10 backdrop-blur-sm' :'text-white hover:text-brand-teal hover:bg-white/10 backdrop-blur-sm'
                  }`}
                >
                  {item.label}
                  {active && (
                    <span className="absolute bottom-0 left-1/2 -translate-x-1/2 w-3/4 h-0.5 bg-brand-teal rounded-full" />
                  )}
                </Link>
              );
            })}
          </nav>

          {/* CTA Button - Desktop */}
          <div className="hidden lg:flex items-center space-x-4">
            <Link
              href="/contact"
              className="px-4 xl:px-6 py-2.5 bg-brand-teal text-white font-body font-bold text-sm rounded-md shadow-button hover:bg-brand-green hover:shadow-lg hover:-translate-y-0.5 transition-smooth active:scale-95"
            >
              Start the Conversation
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            ref={menuButtonRef}
            onClick={toggleMobileMenu}
            className="lg:hidden p-3 text-white hover:bg-white/10 rounded-md transition-smooth min-h-[44px] min-w-[44px] flex items-center justify-center backdrop-blur-sm"
            aria-label={isMobileMenuOpen ? 'Close navigation menu' : 'Open navigation menu'}
            aria-expanded={isMobileMenuOpen}
            aria-controls="mobile-nav-drawer"
          >
            <Icon
              name={isMobileMenuOpen ? 'XMarkIcon' : 'Bars3Icon'}
              size={24}
              variant="outline"
            />
          </button>
        </div>

        {/* Mobile Navigation Drawer */}
        {isMobileMenuOpen && (
          <>
            {/* Backdrop overlay */}
            <div
              className={`lg:hidden fixed inset-0 top-20 md:top-24 bg-black/50 backdrop-blur-sm transition-opacity duration-300 ${
                isAnimating ? 'opacity-100' : 'opacity-0'
              }`}
              aria-hidden="true"
              onClick={closeMenu}
            />

            {/* Drawer panel */}
            <div
              id="mobile-nav-drawer"
              ref={drawerRef}
              role="dialog"
              aria-modal="true"
              aria-label="Navigation menu"
              className={`lg:hidden fixed top-20 md:top-24 left-0 right-0 border-t border-white/10 bg-brand-plum/95 backdrop-blur-md shadow-2xl transition-all duration-300 ease-in-out ${
                isAnimating
                  ? 'opacity-100 translate-y-0' :'opacity-0 -translate-y-4'
              }`}
            >
              <nav
                aria-label="Mobile navigation"
                className="flex flex-col px-3 sm:px-4 py-3 space-y-1"
              >
                {navigationItems.map((item, index) => {
                  const active = isActive(item);
                  return (
                    <Link
                      key={item.href}
                      href={item.href}
                      onClick={handleLinkClick}
                      aria-current={active ? 'page' : undefined}
                      className={`px-4 py-3.5 text-base font-body font-medium rounded-md transition-all duration-300 relative min-h-[44px] flex items-center ${
                        active
                          ? 'text-brand-teal bg-white/10 border-l-4 border-brand-teal backdrop-blur-sm' :'text-white hover:text-brand-teal hover:bg-white/10 backdrop-blur-sm'
                      }`}
                      style={{
                        transitionDelay: isAnimating ? `${index * 50}ms` : '0ms',
                        transform: isAnimating ? 'translateX(0)' : 'translateX(-8px)',
                        opacity: isAnimating ? 1 : 0,
                      }}
                    >
                      {item.label}
                    </Link>
                  );
                })}
                <Link
                  href="/contact"
                  onClick={handleLinkClick}
                  className="mt-3 px-6 py-3.5 bg-brand-teal text-white font-body font-bold text-sm rounded-md shadow-button hover:bg-brand-green text-center transition-smooth active:scale-95 min-h-[44px] flex items-center justify-center"
                  style={{
                    transitionDelay: isAnimating ? `${navigationItems.length * 50}ms` : '0ms',
                    transform: isAnimating ? 'translateX(0)' : 'translateX(-8px)',
                    opacity: isAnimating ? 1 : 0,
                  }}
                >
                  Start the Conversation
                </Link>
              </nav>
            </div>
          </>
        )}
      </div>
    </header>
  );
};

export default Header;